import { AlertController, ModalController } from '@ionic/angular';
import { PAGE } from 'src/app/routes';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName, EventParam } from 'src/app/core/services/analytics/event-names';
import { PromiseHolder } from 'src/app/shared/utils/promise-holder';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ResultDrawerComponent } from 'src/app/features/results/result-drawer/result-drawer.component';
import { ResultDrawerViewModel } from 'src/app/features/results/result-drawer/result-drawer-view-model';
import { RoomService } from 'src/app/core/services/room/room.service';
import { CurrencyPipe } from '@angular/common';
import { Round } from 'src/app/shared/types/round';
import { Room } from 'src/app/model/room/room';
import { TournamentView, WagerApi } from 'src/app/core/api/wager-api';
import { RoomModel } from 'src/app/model/room/room.model';
import { HttpService } from 'src/app/core/services/http/http.service';
import { RoomTimer } from 'src/app/model/room/room-timer';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { TournamentService } from 'src/app/core/services/tournament/tournament.service';
import { RoomStatus } from 'src/app/shared/types/room-status';
import { AlertManagerService } from 'src/app/core/services/alert-stack-manager/alert-stack-manager.service';

export class GameAlert {
  constructor(
    private userId: string,
    private alertController: AlertController,
    private analyticsService: AnalyticsService,
    private translocoService: TranslocoService,
    private themeService: ThemeService,
    private modalController: ModalController,
    private currencyPipe: CurrencyPipe,
    private httpService: HttpService,
    private router: Router,
    private isMobile: boolean,
    private tournamentService?: TournamentService,
    private alertStackManager?: AlertManagerService,
  ) {}

  async scoreNotSubmitted(title: string): Promise<void> {
    const alert = await this.alertController.create({
      header: title,
      message: this.translocoService.translate('service.alert.score-not-submitted-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.ok-button'),
          cssClass: 'alert-button',
          handler: () => {
            this.analyticsService.log(EventName.ClinchGameScoreNotSubmittedAlertAnswer, {
              answer: EventParam.Ok,
            });
            this.router.navigate([PAGE.RESULTS]);
            alert.dismiss();
          },
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
    this.analyticsService.log(EventName.ClinchGameScoreNotSubmittedAlertView);
  }

  async secretsMismatch(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translocoService.translate('pages.game-component.secrets-mismatch-title'),
      message: this.translocoService.translate('pages.game-component.secrets-mismatch-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.ok-button'),
          cssClass: 'alert-button',
          handler: async () => {
            this.router.navigate([PAGE.LOADING]);
          },
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
  }

  async finish(header: string, message: string): Promise<boolean> {
    const alert = await this.alertController.create({
      header,
      message,
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.no-button'),
          cssClass: 'alert-button',
          handler: () => {
            this.analyticsService.log(EventName.ClinchGameFinishAlertAnswer, {
              answer: EventParam.No,
            });
            alert.dismiss();
            resolveChoice(false);
          },
        },
        {
          text: this.translocoService.translate('service.alert.yes-button'),
          cssClass: 'alert-button',
          handler: async () => {
            this.analyticsService.log(EventName.ClinchGameFinishAlertAnswer, {
              answer: EventParam.Yes,
            });
            alert.dismiss();
            resolveChoice(true);
          },
        },
      ],
      backdropDismiss: false,
    });

    // Push the alert to the stack manager
    this.alertStackManager?.push(alert);

    this.analyticsService.log(EventName.ClinchGameFinishAlertView);

    let resolveChoice: (value: boolean) => void;
    const resultPromise = new Promise<boolean>((resolve) => {
      resolveChoice = resolve;
    });

    await alert.present();
    return resultPromise;
  }

  async errorFinishWithExit(
    header: string,
    message: string,
    finish: (message: string) => Promise<void>,
  ): Promise<void> {
    const alert = await this.alertController.create({
      header,
      message,
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.exit-button'),
          cssClass: 'alert-button',
          handler: async () => {
            this.analyticsService.log(EventName.ClinchGameErrorFinishAlertAnswer, {
              answer: EventParam.No,
            });
            alert.dismiss();
            this.router.navigate([PAGE.RESULTS]);
          },
        },
        {
          text: this.translocoService.translate('service.alert.retry-button'),
          cssClass: 'alert-button',
          handler: async () => {
            this.analyticsService.log(EventName.ClinchGameErrorFinishAlertAnswer, {
              answer: EventParam.Yes,
            });
            alert.dismiss();
            await finish(
              this.translocoService.translate('service.alert.finish-sending-score-message'),
            );
          },
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();

    this.analyticsService.log(EventName.ClinchGameErrorFinishAlertView);
  }

  async scoreSubmitted(roomId: string): Promise<void> {
    this.showResultDrawer(roomId);
  }

  async reconnect(promiseHolder: PromiseHolder<void>): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translocoService.translate('service.alert.reconnect-offline-title'),
      message: this.translocoService.translate('service.alert.reconnect-offline-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.reconnect-button'),
          cssClass: 'alert-button',
          handler: () => {
            this.analyticsService.log(EventName.ClinchGameRetryAlertAnswer, {
              answer: EventParam.Ok,
            });
            promiseHolder.resolve();
            alert.dismiss();
          },
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
    this.analyticsService.log(EventName.ClinchGameRetryAlertView);
  }

  async fatal(roomId: string): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translocoService.translate('service.alert.fatal-reload-error-title'),
      message: this.translocoService.translate('service.alert.fatal-reload-error-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.ok-button'),
          cssClass: 'alert-button',
          handler: () => {
            this.analyticsService.log(EventName.ClinchGameFatalAlertAnswer, {
              answer: EventParam.Ok,
            });
            this.showResultDrawer(roomId);
            alert.dismiss();
          },
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
    this.analyticsService.log(EventName.ClinchGameFatalAlertView);
  }

  async noHints(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translocoService.translate('service.alert.no-hints-title'),
      message: this.translocoService.translate('service.alert.no-hints-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.ok-button'),
          cssClass: 'alert-button',
          handler: () => {
            this.analyticsService.log(EventName.ClinchGameNoHintsAlertAnswer, {
              answer: EventParam.Ok,
            });
            alert.dismiss();
          },
        },
      ],
      backdropDismiss: false,
    });

    await alert.present();
    this.analyticsService.log(EventName.ClinchGameNoHintsAlertView);

    const { role } = await alert.onDidDismiss();
    if (role === 'backdrop') {
      this.analyticsService.log(EventName.ClinchGameNoHintsAlertAnswer, {
        answer: EventParam.Dismiss,
      });
    }
  }

  private async showResultDrawer(roomId: string) {
    try {
      const room = await this.fetchRoom(roomId);
      if (!room) {
        this.router.navigate([PAGE.RESULTS]);
        return;
      }

      const tournament: TournamentView | null = await this.fetchRoomTournament(roomId);
      const tournamentReward = room.status() === RoomStatus.Won ? room.reward() : 0;
      const resultDrawerTournamentTitle: string | null =
        tournament?.title && tournament.roundDeadline > Date.now()
          ? this.getDrawerTournamentTitle(tournament?.title, tournamentReward)
          : null;

      // Create ViewModel
      const roomTimer = new RoomTimer(this.translocoService);
      roomTimer.updateRoom(room);
      const resultDrawViewModel = new ResultDrawerViewModel(
        roomTimer,
        room.id,
        room.gameId(),
        Room.statusColor(room.status()),
        false,
        room.formattedFee(this.currencyPipe),
        room.formattedReward(this.currencyPipe),
        room.score(),
        room.status(),
        room.model.roundState,
        null,
        null,
        room.opponentId(),
        room.opponent(),
        room.opponentScore(),
        room.model.opponent?.roundState || Round.NotStarted,
        room.result(),
        room.resultStatus(),
        this.translocoService,
        this.themeService,
        resultDrawerTournamentTitle,
        room.bountyAmount(),
      );
      let modalWillDismiss: () => void;

      const modal = await this.modalController.create({
        component: ResultDrawerComponent,
        componentProps: {
          viewModel: resultDrawViewModel,
          modalWillDismissCallback: (callback: () => void) => {
            modalWillDismiss = callback;
          },
          isAfterGame: true,
          isFreeGame: false,
        },
        initialBreakpoint: this.isMobile ? 1 : undefined,
        breakpoints: this.isMobile ? [0, 1] : undefined,
        handle: false,
        backdropDismiss: false,
        canDismiss: async (_, role) => role !== 'gesture',
        cssClass: this.isMobile ? 'mobile-rounded-modal' : 'desktop-rounded-modal',
      });

      modal.onWillDismiss().then(() => {
        modalWillDismiss?.();
      });

      await modal.present();
    } catch (error) {
      this.router.navigate([PAGE.RESULTS]);
    }
  }

  private async fetchRoom(roomId: string): Promise<Room> {
    const roomModel = (await WagerApi.request(this.httpService).rooms(roomId)).data as RoomModel;
    const roomPath = RoomService.roomPath(roomId, this.userId);
    return new Room(roomModel, roomId, roomPath);
  }

  private async fetchRoomTournament(roomId: string): Promise<TournamentView | null> {
    if (!this.tournamentService) return null;
    const job = this.tournamentService.getRoomTournaments(roomId);
    job.showNoInternetRetry().showSpinner();
    job.addErrorHandler().showFatalError().reloadWindow();
    const roomTournaments = await job.run();
    if (Array.isArray(roomTournaments?.data) && roomTournaments.data.length > 0) {
      return roomTournaments.data[0];
    }
    return null;
  }

  private getDrawerTournamentTitle(tournamentTitle: string, points: number): string {
    return this.translocoService.translate('pages.clinch.result.tournament-points-title', {
      tournamentTitle,
      points: points > 0 ? `+${points}` : '0',
    });
  }
}
